import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Building,
  Calendar,
  Users,
  Link as LinkIcon,
  Mail,
  ChevronLeft,
  User,
  Loader2,
  Clock,
  BriefcaseIcon,
  GraduationCap,
  DollarSign,
  Globe,
  Award,
  Bookmark,
  Share2,
  AlertCircle,
  CheckCircle2,
  FileText,
  UserPlus,
  TrendingUp,
  BookOpen,
  Target,
  Shield,
  Send,
  Code,
  Edit,
  Trash2
} from "lucide-react";

import COMPANY_LOGOS from "../constants/companyLogos";

const CollegeJobDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [currentAlumni, setCurrentAlumni] = useState(null);
  const [isApplying, setIsApplying] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [showApplications, setShowApplications] = useState(false);
  useEffect(() => {
    fetchJobDetails();
  }, [id]);
  const fetchJobDetails = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/jobs/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch job details");
      }

      const data = await response.json();
      setJob(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
// Utility Functions
const formatSalary = (min, max, isNegotiable) => {
  if (!min && !max) return "Not specified";
  if (isNegotiable) return "Negotiable";
  if (min && max) return `₹${formatCurrency(min)} - ₹${formatCurrency(max)}`;
  if (min) return `From ₹${formatCurrency(min)}`;
  return `Up to ₹${formatCurrency(max)}`;
};

const formatCurrency = (amount) => {
  if (amount >= 100000) {
    return `${(amount / 100000).toFixed(1)}L`;
  } else if (amount >= 1000) {
    return `${(amount / 1000).toFixed(1)}K`;
  }
  return amount.toString();
};

const formatDate = (date) => {
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const getTimeAgo = (date) => {
  const seconds = Math.floor((new Date() - new Date(date)) / 1000);
  const intervals = {
    year: 31536000,
    month: 2592000,
    week: 604800,
    day: 86400,
    hour: 3600,
    minute: 60,
    second: 1,
  };

  for (const [unit, secondsInUnit] of Object.entries(intervals)) {
    const interval = Math.floor(seconds / secondsInUnit);
    if (interval > 1) {
      return `${interval} ${unit}s ago`;
    }
    if (interval === 1) {
      return `1 ${unit} ago`;
    }
  }
  return "Just now";
};

const getStatusColor = (status) => {
  const colors = {
    applied: {
      bg: "bg-blue-50",
      text: "text-blue-700",
      border: "border-blue-200",
      icon: "text-blue-500",
    },
    pending: {
      bg: "bg-yellow-50",
      text: "text-yellow-700",
      border: "border-yellow-200",
      icon: "text-yellow-500",
    },
    accepted: {
      bg: "bg-green-50",
      text: "text-green-700",
      border: "border-green-200",
      icon: "text-green-500",
    },
    declined: {
      bg: "bg-red-50",
      text: "text-red-700",
      border: "border-red-200",
      icon: "text-red-500",
    },
    default: {
      bg: "bg-gray-50",
      text: "text-gray-700",
      border: "border-gray-200",
      icon: "text-gray-500",
    },
  };
  return colors[status] || colors.default;
};

const JobMetric = ({ icon: Icon, label, value, className }) => (
  <div className={`flex items-center space-x-2 ${className}`}>
    <Icon className="h-5 w-5 text-gray-400" />
    <div>
      <div className="text-sm font-medium text-gray-900">{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  </div>
);

const StatusBadge = ({ status, className }) => {
  const colors = getStatusColor(status);
  return (
    <span
      className={`
      inline-flex items-center px-3 py-1 rounded-full text-sm font-medium
      ${colors.bg} ${colors.text} border ${colors.border}
      ${className}
    `}
    >
      <CheckCircle2 className={`h-4 w-4 mr-1.5 ${colors.icon}`} />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const SectionHeading = ({ children, className }) => (
  <h3 className={`text-lg font-semibold text-gray-900 mb-4 ${className}`}>
    {children}
  </h3>
);
 // All your handler functions
 const handleDelete = async () => {
  if (!window.confirm('Are you sure you want to delete this job post?')) return;

  try {
    const token = localStorage.getItem('token');
    const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/jobs/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to delete job');
    }

    navigate('/college/jobs');
  } catch (err) {
    setError(err.message);
  }
};



// Loading state
if (loading) {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
    </div>
  );
}

// Error state
if (error || !job) {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <p className="text-red-500 mb-4">{error || 'Job not found'}</p>
        <button
          onClick={() => navigate('/college/jobs')}
          className="text-blue-500 hover:text-blue-700"
        >
          Back to Jobs
        </button>
      </div>
    </div>
  );
}








  const handleApply = async (applicationData) => {
    try {
      setIsApplying(true);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/jobs/${id}/application-status`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(applicationData)
        }
      );
  
      const data = await response.json();
  
      if (!response.ok) {
        throw new Error(data.message || 'Failed to submit application');
      }
  
      // Update local state with the new application data
      setApplicationStatus('applied');
      await fetchJobDetails(); // Refresh job details
      
      return {
        success: true,
        application: data.application
      };
    } catch (err) {
      console.error('Error submitting application:', err);
      return {
        success: false,
        message: err.message
      };
    } finally {
      setIsApplying(false);
    }
  };

  const getJobStats = () => {
    if (!job) return {};

    return {
      totalApplications: job.applications?.length || 0,
      daysLeft: Math.max(
        0,
        Math.ceil(
          (new Date(job.applicationDetails.deadline) - new Date()) /
            (1000 * 60 * 60 * 24)
        )
      ),
      referralBonus: job.referralDetails?.bonus || "N/A",
      experience: `${job.jobDetails.experience.min}-${job.jobDetails.experience.max} years`,
      avgSalary: (job.jobDetails.salary.min + job.jobDetails.salary.max) / 2,
      postedDaysAgo: Math.ceil(
        (new Date() - new Date(job.createdAt)) / (1000 * 60 * 60 * 24)
      ),
    };
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `${job.jobDetails.title} at ${job.company.name}`,
        text: `Check out this job opportunity: ${job.jobDetails.title} at ${job.company.name}`,
        url: window.location.href,
      });
    } else {
      setShowShareMenu(!showShareMenu);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          <p className="text-gray-600 animate-pulse">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto" />
          <p className="text-red-500 font-medium">{error || "Job not found"}</p>
          <button
            onClick={() => navigate("/jobs")}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to Jobs
          </button>
        </div>
      </div>
    );
  }

  const stats = getJobStats();
  const isJobClosed = new Date(job.applicationDetails.deadline) < new Date();
  const isOwnPosting =
    currentAlumni &&
    job.postedBy.model === "Alumni" &&
    job.postedBy.user?.toString() === currentAlumni._id?.toString();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Company Logo Banner */}
      <div className="bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900 h-48">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <button
            onClick={() => navigate("/jobs")}
            className="absolute top-4 left-4 text-white hover:text-gray-200 flex items-center"
          >
            <ChevronLeft className="h-5 w-5 mr-1" />
            Back to Jobs
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Job Header Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-lg overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="h-16 w-16 rounded-xl bg-white shadow-md flex items-center justify-center p-3 border border-gray-100">
                    {COMPANY_LOGOS[job.company.name] ? (
                      <img
                        src={COMPANY_LOGOS[job.company.name]}
                        alt={job.company.name}
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <Building className="h-8 w-8 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div>
                        <h1 className="text-2xl font-bold text-gray-900">
                          {job.jobDetails.title}
                        </h1>
                        <div className="mt-1 flex items-center space-x-2 text-gray-500">
                          <span>{job.company.name}</span>
                          <span>•</span>
                          <span>{job.company.location}</span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setIsSaved(!isSaved)}
                          className="p-2 text-gray-400 hover:text-blue-500 transition-colors"
                        >
                          <Bookmark
                            className={`h-5 w-5 ${
                              isSaved ? "fill-current text-blue-500" : ""
                            }`}
                          />
                        </button>
                        <button
                          onClick={handleShare}
                          className="p-2 text-gray-400 hover:text-blue-500 transition-colors"
                        >
                          <Share2 className="h-5 w-5" />
                        </button>
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-4">
                      <JobMetric
                        icon={BriefcaseIcon}
                        label="Job Type"
                        value={job.jobDetails.type}
                      />
                      <JobMetric
                        icon={GraduationCap}
                        label="Experience"
                        value={`${job.jobDetails.experience.min}-${job.jobDetails.experience.max} years`}
                      />
                      <JobMetric
                        icon={DollarSign}
                        label="Salary"
                        value={formatSalary(
                          job.jobDetails.salary.min,
                          job.jobDetails.salary.max,
                          job.jobDetails.salary.isNegotiable
                        )}
                      />
                      <JobMetric
                        icon={Calendar}
                        label="Application Deadline"
                        value={formatDate(job.applicationDetails.deadline)}
                      />
                    </div>
                  </div>
                </div>

                {/* Status and Action Buttons */}
                <div className="mt-6 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {applicationStatus && (
                      <StatusBadge status={applicationStatus} />
                    )}
                    {job.referralEnabled && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-50 text-purple-700 border border-purple-200">
                        <Award className="h-4 w-4 mr-1.5" />
                        Referral Available
                      </span>
                    )}
                  </div>
                  
                 
  <button
    onClick={() => navigate(`/jobs/edit/${id}`)}
    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
  >
    <Edit className="h-4 w-4 mr-2" />
    Edit Job
  </button>
  <button
            onClick={handleDelete}
            className="inline-flex items-center px-4 py-2 border border-red-600 rounded-lg text-sm font-medium text-gray-50 hover:bg-gray-70 bg-red-600"
  >
    <Trash2 className="h-4 w-4 mr-2" />
    Delete Job
  </button>

                </div>
              </div>
            </motion.div>

            {/* Job Description */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <SectionHeading>Job Description</SectionHeading>
              <div className="prose max-w-none text-gray-600">
                {job.jobDetails.description}
              </div>

              {/* Skills & Qualifications */}
              <div className="mt-8 space-y-6">
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">
                    Required Skills
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {job.jobDetails.skills.map((skill, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700"
                      >
                        <Code className="h-4 w-4 mr-1.5" />
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">
                    Qualifications
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {job.jobDetails.qualifications.map((qual, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-50 text-green-700"
                      >
                        <BookOpen className="h-4 w-4 mr-1.5" />
                        {qual}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Company Overview */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <SectionHeading>About {job.company.name}</SectionHeading>
              <div className="flex items-center space-x-4 mb-4">
                <div className="h-16 w-16 rounded-xl bg-white shadow flex items-center justify-center p-3">
                  {COMPANY_LOGOS[job.company.name] ? (
                    <img
                      src={COMPANY_LOGOS[job.company.name]}
                      alt={job.company.name}
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <Building className="h-8 w-8 text-gray-400" />
                  )}
                </div>
                <div>
                  <h4 className="text-lg font-medium text-gray-900">
                    {job.company.name}
                  </h4>
                  <p className="text-gray-500">{job.company.location}</p>
                </div>
              </div>
              {job.company.website && (
                <a
                  href={job.company.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <Globe className="h-4 w-4 mr-2" />
                  Visit Website
                </a>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Job Overview
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Posted</span>
                  <span className="text-gray-900">
                    {getTimeAgo(job.createdAt)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Applications</span>
                  <span className="text-gray-900">
                    {stats.totalApplications}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Time Left</span>
                  <span
                    className={`font-medium ${
                      stats.daysLeft < 3 ? "text-red-600" : "text-gray-900"
                    }`}
                  >
                    {stats.daysLeft} days
                  </span>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                
                {job.applicationDetails.applicationEmail && (
                  <a
                    href={`mailto:${job.applicationDetails.applicationEmail}`}
                    className="inline-flex items-center w-full px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 mb-2"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Send Email
                  </a>
                )}
                
              </div>
            </div>
            {/* Posted By Section */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Posted By
              </h3>
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <User className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {job.postedBy.model === "Alumni"
                      ? `Alumni`
                      : "College Admin"}
                  </p>
                  <p className="text-xs text-gray-500">
                    Posted on {formatDate(job.createdAt)}
                  </p>
                </div>
              </div>
            </div>

            {/* Applications Section (if own posting) */}
            {isOwnPosting && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Applications
                  </h3>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                    <Users className="h-4 w-4 mr-1.5" />
                    {job.applications?.length || 0}
                  </span>
                </div>

                {job.applications?.length > 0 ? (
                  <div className="space-y-3">
                    {job.applications.map((application) => (
                      <motion.div
                        key={application._id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <User className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {application.alumni.firstName}{" "}
                              {application.alumni.lastName}
                            </p>
                            <div className="flex items-center text-xs text-gray-500">
                              <Clock className="h-3 w-3 mr-1" />
                              {getTimeAgo(application.appliedAt)}
                            </div>
                          </div>
                          <StatusBadge status={application.status} />
                        </div>

                        {application.needsReferral && (
                          <div className="mt-2 flex items-center text-sm text-purple-600">
                            <UserPlus className="h-4 w-4 mr-1.5" />
                            Requested Referral
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                    <p>No applications yet</p>
                  </div>
                )}
              </div>
            )}

            {/* Job Insights */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Job Insights
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-sm text-gray-500">
                    <TrendingUp className="h-4 w-4 mr-2" />
                    Experience Level
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    {job.jobDetails.experience.min}+ years
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center text-sm text-gray-500">
                    <Target className="h-4 w-4 mr-2" />
                    Position
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    {job.jobDetails.type}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center text-sm text-gray-500">
                    <Shield className="h-4 w-4 mr-2" />
                    Verified Posting
                  </div>
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Timeline Section */}
        <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-6">
            Application Timeline
          </h3>
          <div className="relative">
            <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-gray-200"></div>
            <div className="space-y-8">
              <div className="relative flex items-center">
                <div className="absolute -left-2 h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center">
                  <FileText className="h-4 w-4 text-white" />
                </div>
                <div className="ml-10">
                  <h4 className="text-sm font-medium text-gray-900">
                    Job Posted
                  </h4>
                  <p className="text-xs text-gray-500">
                    {formatDate(job.createdAt)}
                  </p>
                </div>
              </div>

              {stats.totalApplications > 0 && (
                <div className="relative flex items-center">
                  <div className="absolute -left-2 h-8 w-8 rounded-full bg-purple-500 flex items-center justify-center">
                    <Users className="h-4 w-4 text-white" />
                  </div>
                  <div className="ml-10">
                    <h4 className="text-sm font-medium text-gray-900">
                      {stats.totalApplications} Applications Received
                    </h4>
                    <p className="text-xs text-gray-500">
                      Latest on{" "}
                      {formatDate(
                        job.applications[job.applications.length - 1]?.appliedAt
                      )}
                    </p>
                  </div>
                </div>
              )}

              <div className="relative flex items-center">
                <div
                  className={`absolute -left-2 h-8 w-8 rounded-full flex items-center justify-center
                  ${isJobClosed ? "bg-red-500" : "bg-yellow-500"}`}
                >
                  <Calendar className="h-4 w-4 text-white" />
                </div>
                <div className="ml-10">
                  <h4 className="text-sm font-medium text-gray-900">
                    {isJobClosed
                      ? "Application Closed"
                      : "Application Deadline"}
                  </h4>
                  <p className="text-xs text-gray-500">
                    {formatDate(job.applicationDetails.deadline)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
   {/* Application Info Card */}

   <div className="bg-white rounded-xl shadow-sm overflow-hidden m-8">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold">Applications</h2>
                  <button
                    onClick={() => setShowApplications(!showApplications)}
                    className="inline-flex items-center px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
                  >
                    <Users className="h-4 w-4 mr-2" />
                    {showApplications ? 'Hide' : 'Show'} Applications
                    <span className="ml-2 bg-gray-100 px-2 py-0.5 rounded-full">
                      {job.applications?.length || 0}
                    </span>
                  </button>
                </div>

                {showApplications && job.applications?.length > 0 && (
                  <div className="space-y-4 ">
                    {job.applications.map((application, index) => (
                      <div
                        key={index}
                        className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100"
                      >
                        <div className="flex items-center mb-4 sm:mb-0">
                          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-medium text-lg">
                            {`${application.alumni.firstName[0]}${application.alumni.lastName[0]}`}
                          </div>
                          <div className="ml-4">
                            <div className="font-medium text-gray-900">
                              {application.alumni.firstName} {application.alumni.lastName}
                            </div>
                            <div className="text-sm text-gray-500 flex items-center mt-1">
                              <Clock className="h-4 w-4 mr-1" />
                              {new Date(application.appliedAt).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          {application.needsReferral && (
                            <div className="flex items-center space-x-2 px-3 py-1 bg-amber-50 text-amber-700 rounded-lg">
                              <Award className="h-4 w-4" />
                              <span className="text-sm font-medium">Needs Referral</span>
                            </div>
                          )}
                          <button className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100">
                            <Mail className="h-5 w-5" />
                          </button>
                          <button className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100">
                            <Share2 className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                </div>

            {job.referralEnabled && (
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-sm overflow-hidden border border-blue-100">
                <div className="p-6">
                  <h2 className="text-xl font-semibold text-blue-900 mb-2">
                    Referral Program
                  </h2>
                  <p className="text-blue-700 text-sm mb-4">
                    This job post has referral program enabled for alumni
                  </p>
                  <button className="w-full inline-flex items-center justify-center px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700">
                    <Award className="h-4 w-4 mr-2" />
                    View Referral Details
                  </button>
                </div>
              </div>
            )}
          </div>
      {/* Share Menu */}
      {showShareMenu && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-lg p-6 max-w-sm w-full mx-4"
          >
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Share Job
            </h3>
            <div className="space-y-4">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  setShowShareMenu(false);
                }}
                className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <LinkIcon className="h-4 w-4 mr-2" />
                Copy Link
              </button>
              <button
                onClick={() => {
                  window.open(
                    `mailto:?subject=Job Opportunity: ${job.jobDetails.title}&body=Check out this job opportunity: ${window.location.href}`
                  );
                  setShowShareMenu(false);
                }}
                className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Mail className="h-4 w-4 mr-2" />
                Share via Email
              </button>
            </div>
            <button
              onClick={() => setShowShareMenu(false)}
              className="mt-4 w-full px-4 py-2 bg-gray-100 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              Cancel
            </button>
          </motion.div>
        </div>
      )}

   
    
    </div>
  );
};

export default CollegeJobDetails;
